import { useContext, useEffect, useState } from "react";

import clsx from "clsx";

import useConfirmModal from "@/src/cashback/LandingPage/useConfirmModal";
import { languageSelector } from "@/src/contexts";
import { useQueryClientCustom, useQueryCustomNoSuspense } from "@/src/query-client-overrides";
import { browserStatics } from "@/src/shared/BrowserStatic";
import { formatAmount, urlFormat } from "@Components/Modules/Helpers";
import { Cashback } from "@Routes/CashbackLinks";
import * as stylex from "@stylexjs/stylex";
import { queryOptions } from "@tanstack/react-query";

import type { IInformationModal } from "../../common/InformationModal";
import { InformationModal } from "../../common/InformationModal";
import type { IEmailConfirmation } from "../../settings/EmailConfirmation";
import { EmailConfirmation, IEmailConfirmationTypes } from "../../settings/EmailConfirmation";
import type { ICashbackChart } from "../CashbackChart";
import CashbackChart from "../CashbackChart";
import type { ICashbackTooltip } from "../CashbackTooltip";
import type { ICashbackWithdraw } from "../CashbackWithdraw";
import { CashbackWithdraw } from "../CashbackWithdraw";
import { ComposableSpacing, globalFontStyles } from "./../../../styles/tokens.stylex";
import { CashbackDashboardContext } from "./CashbackDashboardContext";
import { CashbackMainPageResources } from "./CashbackMainPage.Resources";
import { CashbackMainPageResourcesEn } from "./CashbackMainPage.Resources.En";
import TextLabel from "./TextLabel";
import type { ITransactionManager } from "./TransactionManager";
import TransactionManager from "./TransactionManager";

import * as css from "./CashbackDashboard.module.css";

interface ICashbackResult {
    chartData: ICashbackChart;
    isEmailVerified: boolean;
    withdrawalBlockedModal?: IInformationModal;
    withdrawalMaxCountReachedModal?: IInformationModal;
    emailVerificationModal?: IEmailConfirmation;
    totalWithdrawalAmount?: number;
}
export interface ICashbackDashboard {
    initialData: ICashbackResult;
    withdraw: ICashbackWithdraw;
    transactionManager: ITransactionManager;
    confirmModal?: IInformationModal;
    cashbackTooltipByEmail?: ICashbackTooltip;
}

const cashbackDetailsQueryOptions = (initial?: ICashbackResult) =>
    queryOptions<ICashbackResult | undefined>({
        queryKey: ["cashbackDetails"],
        queryFn: async () => {
            const response = await fetch(urlFormat(Cashback, { action: "Details" }), {
                method: "GET",
            });
            return response.json();
        },
        initialData: initial,
        retry: 1,
    });

export default function CashbackDashboard(props: ICashbackDashboard) {
    const [showWithdrawModal, setShowWithdrawModal] = useState(false);
    const [showWithdrawBlockedModal, setShowWithdrawBlockedModal] = useState(false);
    const [showWithdrawalMaxCountReachedModal, setShowWithdrawalMaxCountReachedModal] = useState(false);
    const [showVerificationEmailAddress, setShowVerificationEmailAddress] = useState(false);

    const { setTotalWithdrawalAmount } = useContext(CashbackDashboardContext);

    const queryClient = useQueryClientCustom();

    const confirmModal = useConfirmModal(props.confirmModal);

    const resources = languageSelector(
        CashbackMainPageResources.cashbackDashboard,
        CashbackMainPageResourcesEn.cashbackDashboard,
    );

    const { language } = browserStatics;

    const { data } = useQueryCustomNoSuspense<ICashbackResult>(
        cashbackDetailsQueryOptions(props.initialData),
    );

    const chartData = data?.chartData;
    const emailVerificationModal = data?.emailVerificationModal;
    const withdrawalBlockedModal = data?.withdrawalBlockedModal;
    const withdrawalMaxCountReachedModal = data?.withdrawalMaxCountReachedModal;

    const shouldShowWithdrawModal = () => {
        if (withdrawalMaxCountReachedModal != null) {
            setShowWithdrawalMaxCountReachedModal(true);
            return;
        }

        if (withdrawalBlockedModal != null) {
            setShowWithdrawBlockedModal(true);
            return;
        }

        if (data.isEmailVerified === false) {
            setShowVerificationEmailAddress(true);
            return;
        }

        setShowWithdrawModal(true);
    };

    const totalWithdrawal =
        data?.totalWithdrawalAmount && data.totalWithdrawalAmount > 0
            ? formatAmount(data.totalWithdrawalAmount, language)
            : null;

    useEffect(() => {
        if (totalWithdrawal) {
            setTotalWithdrawalAmount(totalWithdrawal);
        }
    }, [totalWithdrawal]);

    const onModalClose = () => {
        setShowWithdrawModal(false);
        setShowWithdrawBlockedModal(false);
        setShowWithdrawalMaxCountReachedModal(false);
        setShowVerificationEmailAddress(false);

        queryClient.invalidateQueries(cashbackDetailsQueryOptions());
    };

    const renderVerificationEmail = () =>
        showVerificationEmailAddress &&
        emailVerificationModal && (
            <EmailConfirmation
                {...emailVerificationModal}
                emailConfirmationType={IEmailConfirmationTypes.EmailVerificationType}
                navigationModal={{
                    ...emailVerificationModal.navigationModal,
                    onClose: onModalClose,
                }}
            />
        );

    const renderCashbackChart = () => {
        return (
            <>
                <div className={css.cashbackChart}>
                    {chartData && (
                        <div className={css.headerContainer}>
                            <div
                                className={clsx(
                                    css.cashbackChartContainer,
                                    chartData.automaticWithdrawalsInfo && css.automaticWithdrawalsInfo,
                                )}
                            >
                                <CashbackChart
                                    {...chartData}
                                    onWithdrawalClick={shouldShowWithdrawModal}
                                    cashbackTooltipByEmail={props.cashbackTooltipByEmail}
                                />
                            </div>
                        </div>
                    )}
                </div>
                {showWithdrawModal && (
                    <CashbackWithdraw
                        {...props.withdraw}
                        navigationModal={
                            props.withdraw && props.withdraw.navigationModal
                                ? {
                                      ...props.withdraw.navigationModal,
                                      onClose: onModalClose,
                                  }
                                : undefined
                        }
                    />
                )}
                {showWithdrawBlockedModal && (
                    <InformationModal {...withdrawalBlockedModal} onClose={onModalClose} />
                )}
                {showWithdrawalMaxCountReachedModal && (
                    <InformationModal {...withdrawalMaxCountReachedModal} onClose={onModalClose} />
                )}
                {renderVerificationEmail()}
            </>
        );
    };

    const cashbackValuesWrapperCss = clsx(css.cashbackValuesWrapper, totalWithdrawal && css.hasWithdrawal);

    return (
        <div className={css.cashbackDashboardComponent}>
            <div className={css.headerWrapper}>
                <div className={css.cashbackValues}>
                    <div className={cashbackValuesWrapperCss}>
                        {renderCashbackChart()}
                        {totalWithdrawal && (
                            <TextLabel
                                id="headerTotalWithdrawalInfo"
                                variant="whiteBig"
                                labelText={resources.totalAmountWithdrawalText}
                                text={totalWithdrawal}
                                styleX={[styles.totalWithdrawalAmount, globalFontStyles.BodyL]}
                            />
                        )}
                    </div>
                </div>
                <div className={css.cashbackTransactions}>
                    <TransactionManager {...props.transactionManager} />
                </div>
            </div>
            {confirmModal}
        </div>
    );
}

const styles = stylex.create({
    totalWithdrawalAmount: {
        backgroundImage: "linear-gradient(77deg, #1b2670, #dc2765)",
        display: "flex",
        justifyContent: "space-between",
        borderBottomLeftRadius: 25,
        borderBottomRightRadius: 25,
        paddingBottom: ComposableSpacing.s5,
        paddingLeft: ComposableSpacing.s4,
        paddingRight: ComposableSpacing.s4,
        paddingTop: ComposableSpacing.s5,
    },
});
